import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { AppRoleOption, APP_ROLE_OPTIONS } from 'src/app/app-roles';
import { MenuStateService } from 'src/app/menu-state.service';

interface PermissionGrantRow {
  permission_key: string;
  group_key: string;
  label: string;
  description: string | null;
  target_type: string;
  route_path: string | null;
  sort_order: number;
  active: boolean;
  create_access: boolean;
  read_access: boolean;
  update_access: boolean;
  delete_access: boolean;
}

interface PermissionTargetRow {
  permission_key: string;
  group_key: string;
  label: string;
  description: string | null;
  target_type: string;
  route_path: string | null;
  sort_order: number;
  active: boolean;
  role_template_count: number;
  user_override_count: number;
}

interface PermissionTargetForm {
  permission_key: string;
  group_key: string;
  label: string;
  description: string;
  target_type: string;
  route_path: string;
  sort_order: number;
  active: boolean;
}

interface PermissionGroupSection {
  groupKey: string;
  grants: PermissionGrantRow[];
  enabledTargets: number;
  enabledActions: number;
}

type PermissionAction = 'create_access' | 'read_access' | 'update_access' | 'delete_access';
type AccessPreset = 'none' | 'view' | 'edit' | 'full' | 'custom';
type PermissionManagerTab = 'roles' | 'targets';

@Component({
  selector: 'app-permission-manager',
  templateUrl: './permission-manager.component.html',
  styleUrls: ['./permission-manager.component.scss']
})
export class PermissionManagerComponent implements OnInit {
  private readonly permissionManagerUrl = '/api/permission_manager/permission_manager.php';
  private targetGridApi?: GridApi;

  readonly roles: AppRoleOption[] = APP_ROLE_OPTIONS;
  readonly targetTypes = ['page', 'section', 'action', 'field'];
  readonly permissionActions: Array<{ key: PermissionAction; label: string; short: string }> = [
    { key: 'create_access', label: 'Create', short: 'C' },
    { key: 'read_access', label: 'Read', short: 'R' },
    { key: 'update_access', label: 'Update', short: 'U' },
    { key: 'delete_access', label: 'Delete', short: 'D' },
  ];

  activeTab: PermissionManagerTab = 'roles';
  selectedRoleKey = 'CARRIER';
  copyFromRoleKey = '';
  roleGrants: PermissionGrantRow[] = [];
  filteredRoleGrants: PermissionGrantRow[] = [];
  filterText = '';
  selectedGroup = 'All';
  roleSaving = false;
  roleError = '';
  roleSuccess = '';
  targetsLoading = false;
  collapsedGroups = new Set<string>();

  targetRows: PermissionTargetRow[] = [];
  displayedTargetRows: PermissionTargetRow[] = [];
  targetFilterText = '';
  targetForm: PermissionTargetForm = this.emptyTargetForm();
  targetSaving = false;
  targetLoading = false;
  targetError = '';
  targetSuccess = '';
  showTargetForm = false;

  themeMode: 'light' | 'dark' = 'light';

  targetColumnDefs: ColDef[] = [
    { field: 'group_key', headerName: 'Group', minWidth: 170, flex: 0.75 },
    { field: 'label', headerName: 'Target', minWidth: 220, flex: 1 },
    { field: 'permission_key', headerName: 'Permission Key', minWidth: 280, flex: 1.2 },
    { field: 'target_type', headerName: 'Type', width: 130, valueFormatter: (params: ValueFormatterParams) => this.formatTargetType(params.value) },
    { field: 'route_path', headerName: 'Route', minWidth: 160, flex: 0.7, valueFormatter: (params: ValueFormatterParams) => params.value || '-' },
    { field: 'sort_order', headerName: 'Order', width: 110 },
    { field: 'active', headerName: 'Active', width: 115, valueFormatter: (params: ValueFormatterParams) => this.toBoolean(params.value) ? 'Yes' : 'No' },
    { field: 'role_template_count', headerName: 'Role Templates', width: 150 },
    { field: 'user_override_count', headerName: 'Overrides', width: 125 },
    { field: 'description', headerName: 'Description', minWidth: 320, flex: 1.4, valueFormatter: (params: ValueFormatterParams) => params.value || '-' },
  ];
  targetDefaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: false,
  };

  constructor(
    private http: HttpClient,
    private menuStateService: MenuStateService
  ) { }

  ngOnInit(): void {
    this.menuStateService.themeState$.subscribe((theme) => {
      this.themeMode = theme === 'ams-content-dark' ? 'dark' : 'light';
    });

    this.loadRoleGrants();
    this.loadTargets();
  }

  get groups(): string[] {
    return ['All', ...Array.from(new Set(this.roleGrants.map((grant) => grant.group_key))).sort()];
  }

  get selectedRoleLabel(): string {
    return this.roles.find((role) => role.key === this.selectedRoleKey)?.label ?? this.selectedRoleKey;
  }

  get selectedRole(): AppRoleOption | undefined {
    return this.roles.find((role) => role.key === this.selectedRoleKey);
  }

  get copyFromRoleOptions(): AppRoleOption[] {
    return this.roles.filter((role) => role.key !== this.selectedRoleKey);
  }

  get enabledTargetCount(): number {
    return this.roleGrants.filter((grant) => this.rowHasAnyAccess(grant)).length;
  }

  get enabledActionCount(): number {
    return this.roleGrants.reduce((count, grant) => count + this.actionCountForGrant(grant), 0);
  }

  get groupedPermissionSections(): PermissionGroupSection[] {
    const sections = new Map<string, PermissionGroupSection>();

    this.filteredRoleGrants.forEach((grant) => {
      if (!sections.has(grant.group_key)) {
        sections.set(grant.group_key, {
          groupKey: grant.group_key,
          grants: [],
          enabledTargets: 0,
          enabledActions: 0,
        });
      }

      const section = sections.get(grant.group_key)!;
      section.grants.push(grant);

      if (this.rowHasAnyAccess(grant)) {
        section.enabledTargets++;
      }

      section.enabledActions += this.actionCountForGrant(grant);
    });

    return Array.from(sections.values()).sort((first, second) => first.groupKey.localeCompare(second.groupKey));
  }

  get selectedRoleEnabledPercent(): number {
    if (this.roleGrants.length === 0) {
      return 0;
    }

    return Math.round((this.enabledTargetCount / this.roleGrants.length) * 100);
  }

  setActiveTab(tab: PermissionManagerTab): void {
    this.activeTab = tab;
  }

  selectRole(roleKey: string): void {
    if (this.selectedRoleKey === roleKey) {
      return;
    }

    this.selectedRoleKey = roleKey;
    this.copyFromRoleKey = '';
    this.selectedGroup = 'All';
    this.filterText = '';
    this.collapsedGroups.clear();
    this.loadRoleGrants();
  }

  loadRoleGrants(): void {
    this.targetsLoading = true;
    this.roleError = '';
    this.roleSuccess = '';

    this.http.get<{ grants: PermissionGrantRow[] }>(this.permissionManagerUrl, {
      params: {
        type: 'role_grants',
        role_key: this.selectedRoleKey,
      }
    }).subscribe({
      next: (response) => {
        this.roleGrants = (response?.grants ?? []).map((grant) => this.normalizeGrantRow(grant));
        this.updateDisplayedRoleGrants();
        this.targetsLoading = false;
      },
      error: (error) => {
        this.roleError = error?.error?.message || 'Unable to load role template.';
        this.targetsLoading = false;
      }
    });
  }

  loadTargets(): void {
    this.targetLoading = true;
    this.targetError = '';

    this.http.get<{ targets: PermissionTargetRow[] }>(this.permissionManagerUrl, {
      params: { type: 'targets' }
    }).subscribe({
      next: (response) => {
        this.targetRows = (response?.targets ?? []).map((target) => this.normalizeTargetRow(target));
        this.updateDisplayedTargets();
        this.targetLoading = false;
      },
      error: (error) => {
        this.targetError = error?.error?.message || 'Unable to load permission targets.';
        this.targetLoading = false;
      }
    });
  }

  updateDisplayedRoleGrants(): void {
    const search = this.filterText.trim().toLowerCase();

    this.filteredRoleGrants = this.roleGrants.filter((grant) => {
      const matchesGroup = this.selectedGroup === 'All' || grant.group_key === this.selectedGroup;
      const matchesSearch = !search
        || grant.permission_key.toLowerCase().includes(search)
        || grant.label.toLowerCase().includes(search)
        || (grant.description ?? '').toLowerCase().includes(search);

      return matchesGroup && matchesSearch;
    });
  }

  updateDisplayedTargets(): void {
    const search = this.targetFilterText.trim().toLowerCase();

    this.displayedTargetRows = this.targetRows.filter((target) => !search
      || target.permission_key.toLowerCase().includes(search)
      || target.label.toLowerCase().includes(search)
      || target.group_key.toLowerCase().includes(search)
      || (target.description ?? '').toLowerCase().includes(search)
      || (target.route_path ?? '').toLowerCase().includes(search));
  }

  copyTemplateFromRole(): void {
    if (!this.copyFromRoleKey) {
      return;
    }

    this.roleError = '';
    this.roleSuccess = '';

    this.http.get<{ grants: PermissionGrantRow[] }>(this.permissionManagerUrl, {
      params: {
        type: 'role_grants',
        role_key: this.copyFromRoleKey,
      }
    }).subscribe({
      next: (response) => {
        const sourceRows = new Map((response?.grants ?? []).map((grant) => [grant.permission_key, this.normalizeGrantRow(grant)]));
        this.roleGrants = this.roleGrants.map((grant) => {
          const sourceGrant = sourceRows.get(grant.permission_key);

          if (!sourceGrant) {
            return grant;
          }

          return {
            ...grant,
            create_access: sourceGrant.create_access,
            read_access: sourceGrant.read_access,
            update_access: sourceGrant.update_access,
            delete_access: sourceGrant.delete_access,
          };
        });
        this.updateDisplayedRoleGrants();
        const sourceRoleLabel = this.roles.find((role) => role.key === this.copyFromRoleKey)?.label ?? this.copyFromRoleKey;
        this.roleSuccess = `Copied ${sourceRoleLabel}. Save the template to keep these changes.`;
      },
      error: (error) => {
        this.roleError = error?.error?.message || 'Unable to copy role template.';
      }
    });
  }

  saveRoleTemplate(): void {
    this.roleSaving = true;
    this.roleError = '';
    this.roleSuccess = '';

    this.http.post<{ message: string; grants: PermissionGrantRow[] }>(this.permissionManagerUrl, {
      action: 'save_role_grants',
      role_key: this.selectedRoleKey,
      grants: this.roleGrants.map((grant) => ({
        permission_key: grant.permission_key,
        create_access: grant.create_access,
        read_access: grant.read_access,
        update_access: grant.update_access,
        delete_access: grant.delete_access,
      }))
    }).subscribe({
      next: (response) => {
        this.roleSaving = false;
        this.roleSuccess = response?.message || 'Role template saved.';
        this.roleGrants = (response?.grants ?? this.roleGrants).map((grant) => this.normalizeGrantRow(grant));
        this.updateDisplayedRoleGrants();
      },
      error: (error) => {
        this.roleSaving = false;
        this.roleError = error?.error?.message || 'Unable to save role template.';
      }
    });
  }

  saveTarget(): void {
    this.targetSaving = true;
    this.targetError = '';
    this.targetSuccess = '';

    this.http.post<{ message: string }>(this.permissionManagerUrl, {
      action: 'upsert_target',
      ...this.targetForm,
    }).subscribe({
      next: (response) => {
        this.targetSaving = false;
        this.targetSuccess = response?.message || 'Permission target saved.';
        this.targetForm = this.emptyTargetForm();
        this.showTargetForm = false;
        this.loadRoleGrants();
        this.loadTargets();
      },
      error: (error) => {
        this.targetSaving = false;
        this.targetError = error?.error?.message || 'Unable to save permission target.';
      }
    });
  }

  toggleGrant(row: PermissionGrantRow, action: PermissionAction): void {
    row[action] = !row[action];
    this.roleError = '';
    this.roleSuccess = '';
  }

  applyPresetToFiltered(preset: AccessPreset): void {
    if (preset === 'custom') {
      return;
    }

    this.filteredRoleGrants.forEach((grant) => this.applyPreset(grant, preset));
    this.roleError = '';
    this.roleSuccess = `Applied ${this.accessPresetLabel(preset)} to ${this.filteredRoleGrants.length} visible targets. Save the template to keep it.`;
  }

  applyPresetToGroup(section: PermissionGroupSection, preset: AccessPreset): void {
    if (preset === 'custom') {
      return;
    }

    section.grants.forEach((grant) => this.applyPreset(grant, preset));
    this.roleError = '';
    this.roleSuccess = `Applied ${this.accessPresetLabel(preset)} to ${section.groupKey}. Save the template to keep it.`;
  }

  setRowPreset(row: PermissionGrantRow, preset: Exclude<AccessPreset, 'custom'>): void {
    this.applyPreset(row, preset);
    this.roleError = '';
    this.roleSuccess = '';
  }

  getAccessPreset(row: PermissionGrantRow): AccessPreset {
    if (!row.create_access && !row.read_access && !row.update_access && !row.delete_access) {
      return 'none';
    }

    if (!row.create_access && row.read_access && !row.update_access && !row.delete_access) {
      return 'view';
    }

    if (!row.create_access && row.read_access && row.update_access && !row.delete_access) {
      return 'edit';
    }

    if (row.create_access && row.read_access && row.update_access && row.delete_access) {
      return 'full';
    }

    return 'custom';
  }

  accessPresetLabelForRow(row: PermissionGrantRow): string {
    return this.accessPresetLabel(this.getAccessPreset(row));
  }

  actionCountForGrant(row: PermissionGrantRow): number {
    return this.permissionActions.reduce((count, action) => count + (row[action.key] ? 1 : 0), 0);
  }

  isGroupCollapsed(groupKey: string): boolean {
    return this.collapsedGroups.has(groupKey);
  }

  toggleGroup(groupKey: string): void {
    if (this.collapsedGroups.has(groupKey)) {
      this.collapsedGroups.delete(groupKey);
      return;
    }

    this.collapsedGroups.add(groupKey);
  }

  expandAllGroups(): void {
    this.collapsedGroups.clear();
  }

  collapseAllGroups(): void {
    this.groupedPermissionSections.forEach((section) => this.collapsedGroups.add(section.groupKey));
  }

  toggleTargetForm(): void {
    this.showTargetForm = !this.showTargetForm;
    this.targetError = '';
    this.targetSuccess = '';
  }

  onTargetGridReady(params: GridReadyEvent): void {
    this.targetGridApi = params.api;
  }

  toggleTheme(): void {
    this.menuStateService.toggleTheme();
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  private rowHasAnyAccess(row: PermissionGrantRow): boolean {
    return row.create_access || row.read_access || row.update_access || row.delete_access;
  }

  private applyPreset(row: PermissionGrantRow, preset: Exclude<AccessPreset, 'custom'>): void {
    row.create_access = preset === 'full';
    row.read_access = preset === 'view' || preset === 'edit' || preset === 'full';
    row.update_access = preset === 'edit' || preset === 'full';
    row.delete_access = preset === 'full';
  }

  private accessPresetLabel(preset: AccessPreset): string {
    switch (preset) {
      case 'none':
        return 'No Access';
      case 'view':
        return 'View';
      case 'edit':
        return 'Edit';
      case 'full':
        return 'Full';
      default:
        return 'Custom';
    }
  }

  private formatTargetType(value: unknown): string {
    const text = String(value ?? '');
    return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '-';
  }

  private normalizeGrantRow(grant: PermissionGrantRow): PermissionGrantRow {
    return {
      ...grant,
      active: this.toBoolean(grant.active),
      create_access: this.toBoolean(grant.create_access),
      read_access: this.toBoolean(grant.read_access),
      update_access: this.toBoolean(grant.update_access),
      delete_access: this.toBoolean(grant.delete_access),
    };
  }

  private normalizeTargetRow(target: PermissionTargetRow): PermissionTargetRow {
    return {
      ...target,
      active: this.toBoolean(target.active),
      sort_order: Number(target.sort_order ?? 0),
      role_template_count: Number(target.role_template_count ?? 0),
      user_override_count: Number(target.user_override_count ?? 0),
    };
  }

  private toBoolean(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 't' || value === 'true';
  }

  private emptyTargetForm(): PermissionTargetForm {
    return {
      permission_key: '',
      group_key: '',
      label: '',
      description: '',
      target_type: 'action',
      route_path: '',
      sort_order: 0,
      active: true,
    };
  }
}
